import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: ''
  });

  const [errors, setErrors] = useState({});

  const services = [
    'Interior Painting',
    'Exterior Painting',
    'Texture Finishes',
    'Renovation & Remodeling',
    'Color Consultation',
    'Commercial Projects'
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.phone.trim() && !/^[0-9]{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const whatsappNumber = '919929176618'; // +91 9929176618 without the +

      const messageLines = [
        `New enquiry from BLS Paints website:`,
        ``,
        `Name: ${formData.name}`,
        `Phone: ${formData.phone.trim() || 'Not provided'}`,
        `Service: ${formData.service}`,
        `Message: ${formData.message}`
      ];

      const whatsappMessage = encodeURIComponent(messageLines.join('\n'));
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

      window.location.href = whatsappUrl;
    } else {
      toast.error('Please fill in all required fields correctly.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section id="contact" className="contact-section section-padding">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">Let's Transform Your Space Together</p>
        </div>

        <div className="contact-content">
          {/* Contact Information */}
          <div className="contact-info-wrapper">
            <div className="contact-info-card">
              <h3 className="contact-info-title">Contact Information</h3>
              <p className="contact-info-text">
                Reach out to us for a free consultation. We're here to bring your vision to life.
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <Phone className="contact-detail-icon" size={24} />
                  <div>
                    <div className="contact-detail-label">Phone</div>
                    <a href="tel:+919929176618" className="contact-detail-value">+91 9929176618</a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <Mail className="contact-detail-icon" size={24} />
                  <div>
                    <div className="contact-detail-label">Email</div>
                    <a href="mailto:Blspaintsjaipur@gmail.com" className="contact-detail-value">
                      Blspaintsjaipur@gmail.com
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <Clock className="contact-detail-icon" size={24} />
                  <div>
                    <div className="contact-detail-label">Business Hours</div>
                    <div className="contact-detail-value">24 Hours</div>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <MapPin className="contact-detail-icon" size={24} />
                  <div>
                    <div className="contact-detail-label">Location</div>
                    <div className="contact-detail-value">India</div>
                  </div>
                </div>
              </div>

              <div className="trust-badge">
                <div className="badge-text">Licensed & Insured</div>
                <div className="badge-text">Quality Guaranteed</div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Your Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone Number (optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`form-input ${errors.phone ? 'error' : ''}`}
                  placeholder="9898989898"
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="service" className="form-label">Service Required *</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className={`form-select ${errors.service ? 'error' : ''}`}
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
                {errors.service && <span className="error-message">{errors.service}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-textarea ${errors.message ? 'error' : ''}`}
                  rows="5"
                  placeholder="Tell us about your project..."
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button type="submit" className="btn-primary submit-btn">
                <Send size={20} />
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;